import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppBaseScreen from "@toothfairy/shared-ui/AppBaseScreen";
import AppCard from "@toothfairy/shared-ui/AppCard";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Platform,
  StyleSheet,
  TouchableWithoutFeedback,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import {
  isFirstTrialStarted,
  isUserEnabledToOperate,
  isUserWithNoPaymentMethod,
} from "../../../App";
import AppJoyRide from "../../Components/Joyride";
import PageTitle from "../../Components/PageTitle";
import ToothFairyButton from "../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../../Components/ToothFairyComponents/ToothFairyModal";
import ToothFairySwitch from "../../Components/ToothFairyComponents/ToothFairySwitch";
import Constants from "../../Constants";
import useUserHelperPreferences from "../../Hooks/useUserHelperPreferences";
import useWorkspaces from "../../Hooks/useWorkspaces";
import { useHistory } from "../../Router";
import { isLargeScreen } from "../MainViewContainer";
import WorkspaceChip from "./Components/WorkspaceChip";
import WorkspaceModalContent from "./Components/WorkspaceModalContent";
import ToothFairyLoadingPlaceholder from "../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import utils from "../Common/utils";
const extraDay = 24 * 60 * 60 * 1000;
const steps = [
  {
    target: ".create_workspace",
    content: "To get started, click here to create a new workspace.",
    placement: "left",
    disableBeacon: true,
  },
  {
    target: ".select_workspace",
    disableBeacon: true,
    content:
      "Once you create a workspace, you can select it here. All the workspaces you will be invited to will be displayed here as well!",
  },
];

const WorkspacesScreen = ({
  workspaces,
  handleWorkSpaceCreation,
  handleWorkspaceEdit,
  handleWorkspaceDeletion,
  handleWorkspaceSelection,
  handleFormSubmit,
  initialValues,
  formValidationSchema,
  setIsModalOpen,
  setInitialValues,
  isModalOpen,
  modalContentKey,
  setModalContentKey,
  selectedWorkspace,
  setSelectedWorkspace,
  setShareInputValue,
  shareInputValue,
  handleMembersChange,
  loadMembers,
  shareMembers,
  cancelPlanChange,
  targetPlan,
  setTargetPlan,
  workspaceMutationInProgress,
  onPlanSelection,
  onValuesChange,
  onSubmit,
  forceEnabled,
  handlePaymentAttempt = () => {},
  paymentReattempted,
  isReattemptingPayment,
  ...props
}) => {
  const formRef = useRef();
  const { currentTheme } = AppTheme.useTheme();
  const dimensions = useWindowDimensions();
  const [isMouseOverNewWorkspace, setIsMouseOverNewWorkspace] = useState(false);
  const isNewWorkspaceDisabled =
    isFirstTrialStarted() && !isUserEnabledToOperate();
  const { user } = AppUser.useUser();
  const { t } = useTranslation();
  const [showShareInput, setShareInput] = useState(false);
  const coreViewHeight = dimensions.height - 200;
  const _styles = styleGenerator(
    currentTheme,
    coreViewHeight,
    dimensions,
    isLargeScreen()
  );
  const showBillingRequired = false;
  // (isUserWithNoPaymentMethod() || !isBillingAddressSet()) && !isSSO();
  const history = useHistory();
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const {
    returnCleanedWorkspaces,
    getDisabledWorkspaces,
    isWorkspaceDisabled,
    isWorkspaceInCreation,
  } = useWorkspaces();
  // what is this?
  const { state } = AppStateManager.useAppStateManager();

  const { updateUserHelperPreferences, isUserHelperToBeDisplayed } =
    useUserHelperPreferences();
  const disabledWorkspacesCount = getDisabledWorkspaces(workspaces)?.length;

  // Determine layout type
  const cleanedWorkspaces = returnCleanedWorkspaces(
    workspaces,
    includeDisabled
  );
  const isSingleWorkspace = cleanedWorkspaces?.length === 1;

  // Hero layout components
  const SingleWorkspaceHero = ({ workspace }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <TouchableOpacity
        style={[
          _styles.heroContainer,
          {
            borderColor: isHovered
              ? currentTheme?.theme?.primary
              : currentTheme?.theme?.border_color,
          },
        ]}
        onPress={() => handleWorkspaceSelection(workspace.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View
          style={[
            _styles.heroCard,
            {
              borderColor: isHovered
                ? currentTheme?.theme?.primary
                : currentTheme?.theme?.border_color,
            },
          ]}
        >
          <View style={_styles.heroHeader}>
            <View style={_styles.heroTitleSection}>
              <AppIcon
                icon={AppAssets.icons.workspace}
                size={40}
                color={currentTheme?.theme?.primary}
                style={{ marginRight: 15 }}
              />
              <View>
                <AppText style={_styles.heroTitle}>{workspace.title}</AppText>
                <AppText style={_styles.heroSubtitle}>
                  Your active workspace
                </AppText>
              </View>
            </View>
          </View>

          <View style={_styles.heroStats}>
            <View style={_styles.statCard}>
              <AppIcon
                icon={AppAssets.icons.users}
                size={24}
                color={currentTheme?.theme?.primary}
              />
              <AppText style={_styles.statNumber}>
                {workspace.users?.length || 1}
              </AppText>
              <AppText style={_styles.statLabel}>Members</AppText>
            </View>
            <View style={_styles.statCard}>
              <AppIcon
                icon={AppAssets.icons.subscriptionType}
                size={24}
                color={currentTheme?.theme?.primary}
              />
              <AppText style={_styles.statNumber}>
                {utils.capitalizeFirstLetter(workspace.subscriptionType)}
              </AppText>
              <AppText style={_styles.statLabel}>Plan</AppText>
            </View>
            <View style={_styles.statCard}>
              <AppIcon
                icon={AppAssets.icons.domain}
                size={24}
                color={currentTheme?.theme?.primary}
              />
              <AppText style={_styles.statNumber}>
                {utils.capitalizeFirstLetter(workspace.domain) || "Default"}
              </AppText>
              <AppText style={_styles.statLabel}>Business Type</AppText>
            </View>
          </View>

          <View style={_styles.heroActions}>
            {/* <ToothFairyButton
            title="Create New Workspace"
            variant="outline"
            onPress={handleWorkSpaceCreation}
            isDisabled={isNewWorkspaceDisabled}
            style={_styles.heroSecondaryButton}
          /> */}
            {/* {workspace.userID === user.id && (
            <ToothFairyButton
              title="Workspace Settings"
              variant="outline"
              onPress={() => handleWorkspaceEdit(workspace)}
              style={_styles.heroSecondaryButton}
            />
          )} */}
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const QuickActions = ({ workspace }) => {
    const [hoveredAction, setHoveredAction] = useState(null);

    const ActionCard = ({ icon, label, onPress, actionKey }) => {
      const isHovered = hoveredAction === actionKey;
      return (
        <TouchableOpacity
          style={[
            _styles.actionCard,
            {
              borderColor: isHovered
                ? currentTheme?.theme?.primary
                : currentTheme?.theme?.border_color,
            },
          ]}
          onPress={onPress}
          onMouseEnter={() => setHoveredAction(actionKey)}
          onMouseLeave={() => setHoveredAction(null)}
        >
          <AppIcon icon={icon} size={32} color={currentTheme?.theme?.primary} />
          <AppText style={_styles.actionLabel}>{label}</AppText>
        </TouchableOpacity>
      );
    };

    return (
      <View style={_styles.quickActionsContainer}>
        <AppText style={_styles.sectionTitle}>Quick Actions</AppText>
        <View style={_styles.actionGrid}>
          <ActionCard
            icon={AppAssets.icons.taskAgent}
            label="Start Chat"
            onPress={() => history.push(`/workspaces/${workspace.id}/chats`)}
            actionKey="chat"
          />
          <ActionCard
            icon={AppAssets.icons.notes}
            label="Upload Docs"
            onPress={() =>
              history.push(`/workspaces/${workspace.id}/documents`)
            }
            actionKey="docs"
          />
          <ActionCard
            icon={AppAssets.icons.hire}
            label="Hire Agents"
            onPress={() =>
              history.push(
                `/workspaces/${workspace.id}/settings/roles_settings/hire`
              )
            }
            actionKey="hire"
          />
          <ActionCard
            icon={AppAssets.icons.settings}
            label="Settings"
            onPress={() =>
              history.push(
                `/workspaces/${workspace.id}/settings/roles_settings/`
              )
            }
            actionKey="settings"
          />
        </View>
      </View>
    );
  };

  const RecentActivity = () => (
    <View style={_styles.activityContainer}>
      <AppText style={_styles.sectionTitle}>Recent Activity</AppText>
      <View style={_styles.activityList}>
        <View style={_styles.activityItem}>
          <AppIcon
            icon={AppAssets.icons.chatIcon}
            size={20}
            color={currentTheme?.theme?.primary}
          />
          <AppText style={_styles.activityText}>Last chat: 2 hours ago</AppText>
        </View>
        <View style={_styles.activityItem}>
          <AppIcon
            icon={AppAssets.icons.file}
            size={20}
            color={currentTheme?.theme?.primary}
          />
          <AppText style={_styles.activityText}>
            Document uploaded: Yesterday
          </AppText>
        </View>
        <View style={_styles.activityItem}>
          <AppIcon
            icon={AppAssets.icons.trainAI}
            size={20}
            color={currentTheme?.theme?.primary}
          />
          <AppText style={_styles.activityText}>
            Training completed: 3 days ago
          </AppText>
        </View>
      </View>
    </View>
  );

  return state?.workspacesLoading ? (
    <ToothFairyLoadingPlaceholder
      caption="Loading workspaces..."
      brandedAnimation
    />
  ) : (
    <AppBaseScreen isScrollable={false}>
      <AppJoyRide
        handleCallback={(e) => {
          if (e?.status == "finished" || e?.status == "skipped") {
            updateUserHelperPreferences(user, { workspaceTourCompleted: true });
          }
        }}
        run={
          isUserHelperToBeDisplayed("workspaceTourCompleted") && !isModalOpen
        }
        steps={steps}
      />

      <View
        style={{ paddingHorizontal: 20, paddingVertical: 10, height: "100%" }}
      >
        <View>
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              marginTop: -5,
            }}
          >
            <PageTitle
              style={{ marginTop: 10 }}
              accessibilityLabel="workspaces-title"
            >
              Workspaces
            </PageTitle>
            {workspaces?.length > 0 && (
              <div
                className="create_workspace"
                onClick={handleWorkSpaceCreation}
                style={{
                  cursor: "pointer",
                }}
              >
                <ToothFairyButton
                  paddingLeft={10}
                  paddingRight={10}
                  style={{ marginTop: 10 }}
                  isDisabled={isNewWorkspaceDisabled}
                  title="New workspace"
                  onPress={handleWorkSpaceCreation}
                />
              </div>
            )}

            {disabledWorkspacesCount > 0 && (
              <ToothFairySwitch
                wrapperStyle={{
                  marginTop: 10,
                  paddingVertical: 16,
                  paddingHorizontal: 10,
                }}
                accessibilityLabel="inactiveWorkspacesSwitcher"
                label={`${t("Include disabled")} (${disabledWorkspacesCount})`}
                isTicked={includeDisabled}
                displayIsTicked={true}
                onChange={() => {
                  setIncludeDisabled(!includeDisabled);
                }}
              />
            )}
          </View>
          {showBillingRequired && (
            <AppText
              numberOfLines={4}
              color={currentTheme?.theme?.orange}
              fontWeight="bold"
              clickableViewWrapperStyle={{
                marginTop: 20,
              }}
              isClickable
              onPress={() => {
                history.push("/profile/billing");
              }}
            >
              {isFirstTrialStarted()
                ? "BillingSetupRequiredWhileOnTrial"
                : isUserWithNoPaymentMethod()
                ? "GenericBillingSetupRequired"
                : "GenericBillingAddressSetupRequired"}
            </AppText>
          )}
        </View>
        <View style={_styles.list}>
          <View
            style={{
              flexWrap: "wrap",
              justifyContent: "flex-start",
              flexDirection: "row",
            }}
            items={workspaces}
            contentContainerStyle={{
              padding: 10,
            }}
          >
            {workspaces?.length > 0 ? (
              isSingleWorkspace ? (
                // Hero layout for single workspace
                <View style={_styles.singleWorkspaceLayout}>
                  <View style={_styles.supplementaryContent}>
                    <SingleWorkspaceHero workspace={cleanedWorkspaces[0]} />
                    <QuickActions workspace={cleanedWorkspaces[0]} />
                  </View>
                </View>
              ) : (
                // Original grid layout for multiple workspaces
                returnCleanedWorkspaces(workspaces, includeDisabled)?.map(
                  (item) => (
                    <div className="select_workspace" key={item.id}>
                      <AppCard
                        isHoverable
                        accessibilityLabel={`workspace-card-${item.id}`}
                        key={item.id}
                        headerContainerStyle={{
                          alignItems: "center",
                        }}
                        style={{
                          marginHorizontal: 20,
                          borderColor: currentTheme?.theme?.app_outline_color,
                        }}
                        onCardPress={() => {
                          if (isWorkspaceDisabled(item))
                            history.push("/profile/billing");
                          else handleWorkspaceSelection(item?.id);
                        }}
                        disabled={isWorkspaceInCreation(item)}
                        cardWidth={300}
                        cardHeight={260}
                        heading={item?.title}
                        shadowColor={currentTheme?.theme?.transparent}
                        footerHeadingStyle={{
                          fontSize: currentTheme.theme.small_font_size,
                          fontWeight: "normal",
                          paddingBottom: 10,
                          maxWidth: 170,
                        }}
                        // rightIconOnPress={() => {
                        //   user.id === item?.userID && handleWorkspaceEdit(item);
                        // }}
                        headingStyle={{
                          maxWidth: 240,
                          fontSize: 18,
                        }}
                        subFooterHeadingStyle={{
                          maxWidth: 170,
                        }}
                        rightIconSize={20}
                        rightIconDisplayed
                        isRightIconClickable={
                          item?.disabled !== true &&
                          new Date(item?.activeUntil) > Date.now()
                        }
                        // rightIconName={
                        //   user.id === item?.userID && AppAssets.icons.info
                        // }
                        childrenViewStyle={{
                          justifyContent: "flex-end",
                        }}
                      >
                        <View
                          style={{
                            flexDirection: "column",
                            justifyContent: "flex-end",
                            flexWrap: "wrap",
                            height: 110,
                          }}
                        >
                          {/* <View style={{ flexDirection: "row" }}> */}
                          {item?.userID === user.id && (
                            <WorkspaceChip
                              label={"Owner"}
                              leftIcon={AppAssets.icons.admin}
                              backgroundColor={currentTheme?.theme?.primary}
                              fontColor={currentTheme?.theme?.white}
                            />
                          )}

                          {item?.users?.length > 1 && (
                            <WorkspaceChip
                              label={`${item?.users?.length} members`}
                              leftIcon={AppAssets.icons.users}
                            />
                          )}
                          <WorkspaceChip
                            label={
                              utils.capitalizeFirstLetter(item?.domain) ||
                              "Default"
                            }
                            leftIcon={AppAssets.icons.domain}
                          />
                          {/* </View> */}

                          <View style={{ flexDirection: "row" }}>
                            <WorkspaceChip
                              label={utils.capitalizeFirstLetter(
                                utils.adaptSubscriptionName(
                                  item?.subscriptionType
                                )
                              )}
                              leftIcon={AppAssets.icons.subscriptionType}
                              fontColor={
                                (item?.subscriptionType == "pro" ||
                                  item?.subscriptionType == "enterprise") &&
                                currentTheme?.theme?.orange
                              }
                            />
                          </View>
                          {user?.stripeCustomerData?.subscriptionIdOnTrial !=
                            null &&
                            user?.stripeCustomerData?.subscriptionIdOnTrial ===
                              item?.subscriptionId && (
                              <WorkspaceChip
                                label={"Trial"}
                                leftIcon={AppAssets.icons.trial}
                                backgroundColor={currentTheme?.theme?.orange}
                                fontColor={currentTheme?.theme?.white}
                              />
                            )}
                          {/* <WorkspaceChip
                          label={capitalizeFirstLetter(item?.baseModel)}
                          leftIcon={AppAssets.icons.enableAI}
                        /> */}

                          {item?.activeUntil &&
                            (item?.disabled === true ||
                              new Date(item?.activeUntil).getTime() + extraDay <
                                Date.now()) && (
                              <>
                                <WorkspaceChip
                                  isClickable
                                  isHoverable={false}
                                  onPress={() => {
                                    if (item?.userID == user?.id)
                                      history.push("/profile/billing");
                                  }}
                                  backgroundColor={currentTheme?.theme?.red}
                                  fontColor={currentTheme?.theme?.white}
                                  label={`Payment ${
                                    item?.paymentFailed ? "failed" : "required"
                                  }`}
                                  leftIcon={AppAssets.icons.lock}
                                />
                                {(!isUserWithNoPaymentMethod() ||
                                  user?.stripeCustomerData?.balance < 0) &&
                                !paymentReattempted &&
                                !isReattemptingPayment &&
                                item?.userID == user?.id ? (
                                  <AppText
                                    isClickable
                                    onPress={() => {
                                      handlePaymentAttempt(item);
                                    }}
                                    wrapperStyle={{
                                      marginVertical: 9,
                                      marginLeft: 11,
                                    }}
                                    style={{
                                      fontWeight: "bold",
                                      textDecoration: "underline",
                                      fontSize:
                                        currentTheme?.theme
                                          ?.extra_small_font_size,
                                      marginLeft: 10,
                                    }}
                                    iconSize={14}
                                    color={currentTheme?.theme?.orange}
                                    leftIcon={AppAssets.icons.reload}
                                  >
                                    Retry payment
                                  </AppText>
                                ) : isReattemptingPayment == item?.id &&
                                  item?.userID == user?.id ? (
                                  <AppText
                                    wrapperStyle={{
                                      marginVertical: 9,
                                      marginLeft: 11,
                                    }}
                                    style={{
                                      fontWeight: "bold",
                                      textDecoration: "underline",
                                      fontSize:
                                        currentTheme?.theme
                                          ?.extra_small_font_size,
                                      marginLeft: 10,
                                    }}
                                    iconSize={14}
                                    color={currentTheme?.theme?.orange}
                                    leftIcon={AppAssets.icons.progress}
                                  >
                                    Attempting payment
                                  </AppText>
                                ) : (
                                  <></>
                                )}
                              </>
                            )}
                          {!item?.activeUntil && (
                            <WorkspaceChip
                              showProgressIndicator
                              label={"Setting up"}
                              backgroundColor={currentTheme?.theme?.purple}
                              fontColor={currentTheme?.theme?.white}
                              leftIcon={AppAssets.icons.progress}
                            />
                          )}
                        </View>
                      </AppCard>
                    </div>
                  )
                )
              )
            ) : (
              <div
                className="select_workspace"
                style={{
                  margin: "auto",
                }}
              >
                <TouchableWithoutFeedback
                  disabled={isNewWorkspaceDisabled || showBillingRequired}
                  onPress={
                    !showBillingRequired
                      ? handleWorkSpaceCreation
                      : () => {
                          history.push("/profile/billing");
                        }
                  }
                >
                  <AppPlaceholderView
                    onMouseEnter={() => setIsMouseOverNewWorkspace(true)}
                    onMouseLeave={() => setIsMouseOverNewWorkspace(false)}
                    accessibilityLabel="workspaces-placeholder"
                    fontColor={
                      !isNewWorkspaceDisabled && currentTheme?.theme?.primary
                    }
                    wrapperStyle={{
                      marginHorizontal: "auto",
                      marginTop: 120,
                      borderColor:
                        isMouseOverNewWorkspace && !isNewWorkspaceDisabled
                          ? currentTheme?.theme?.primary
                          : currentTheme?.theme?.border_color,
                      borderWidth: 1,
                      borderRadius: 10,
                      padding: 50,
                      maxWidth: 400,
                      maxHeight: 260,
                      cursor: !isNewWorkspaceDisabled ? "pointer" : "default",
                    }}
                    size={30}
                    // fontColor={currentTheme?.theme?.white}
                    // color={currentTheme?.theme?.white}
                    icon={AppAssets.icons.fairy}
                    mainCaption={
                      showBillingRequired
                        ? "WorkspacesBillingEmptyPlaceholder"
                        : "WorkspacesEmptyPlaceholder"
                    }
                    subCaption={
                      showBillingRequired
                        ? "WorkspacesBillingRequiredPlaceholderSub"
                        : "WorkspacesEmptyPlaceholderSub"
                    }
                  />
                </TouchableWithoutFeedback>
              </div>
            )}
          </View>
        </View>
        {isModalOpen && (
          <ToothFairyModal
            headerContainerStyle={{
              paddingTop: 20,
              paddingHorizontal: 20,
            }}
            scrollViewStyle={{
              margin: 20,
              width: "95%",
            }}
            modalBackdrop={true}
            modalBackdropStatic={true}
            modalStyle={_styles.modalInput}
            modalTitle={
              modalContentKey === "NEW"
                ? "Create your workspace"
                : modalContentKey === "UPDATE" || modalContentKey === "DELETE"
                ? `Edit workspace: ${selectedWorkspace?.title}`
                : modalContentKey === "BILLING"
                ? "Billing"
                : ""
            }
            isCloseButtonVisible
            closeModal={() => {
              formRef?.current?.resetForm();
              setIsModalOpen(false);
              setInitialValues(Constants.initialWorkspaceValues);
            }}
            isModalVisible={isModalOpen}
            transparent={true}
          >
            <WorkspaceModalContent
              ref={formRef}
              showShareInput={showShareInput}
              setShareInput={setShareInput}
              modalContentKey={modalContentKey}
              handleFormSubmit={handleFormSubmit}
              formValidationSchema={formValidationSchema}
              initialValues={initialValues}
              shareInputValue={shareInputValue}
              setShareInputValue={setShareInputValue}
              handleMembersChange={handleMembersChange}
              setModalContentKey={setModalContentKey}
              setIsModalOpen={setIsModalOpen}
              handleWorkspaceDeletion={handleWorkspaceDeletion}
              loadMembers={loadMembers}
              shareMembers={shareMembers}
              cancelPlanChange={cancelPlanChange}
              targetPlan={targetPlan}
              setTargetPlan={setTargetPlan}
              selectedWorkspace={selectedWorkspace}
              workspaceMutationInProgress={workspaceMutationInProgress}
              onPlanSelection={onPlanSelection}
              onValuesChange={onValuesChange}
              onSubmit={onSubmit}
              forceEnabled
            />
          </ToothFairyModal>
        )}
      </View>
    </AppBaseScreen>
  );
};
const styleGenerator = (
  currentTheme,
  coreViewHeight,
  dimensions,
  isLargeScreen
) => {
  return StyleSheet.create({
    title: { paddingRight: 10, flex: 1 },
    submitBotton: {
      marginTop: 20,
      alignSelf: "center",
      minWidth: 200,
    },
    list: {
      zIndex: -1,
      flex: 1,
      marginRight: -5,
      marginLeft: -10,
      overflow: "auto",
    },
    workspaceItem: {
      alignSelf: "flex-start",
      flexDirection: "row",
      flex: 1,
      paddingTop: 10,
    },
    modalInput: {
      maxHeight: Math.min(dimensions.height - 40, 680),
      height: "auto",
      marginHorizontal: Platform.OS === "web" && isLargeScreen ? "20%" : "auto",
      marginVertical: "auto",
      width: Platform.OS === "web" && isLargeScreen ? "60%" : "95%",
    },
    // New hero layout styles
    singleWorkspaceLayout: {
      flex: 1,
      padding: 20,
      width: "100%",
    },
    heroContainer: {
      flex: 0.7,
      height: "100%",
      cursor: "pointer",
      borderRadius: 12,
      borderWidth: 2,
      transition: "border-color 0.2s ease",
    },
    heroCard: {
      backgroundColor: currentTheme?.theme?.backgroundColor,
      borderRadius: 10,
      padding: 30,
      borderWidth: 2,
      transition: "border-color 0.2s ease",
      shadowColor: currentTheme?.theme?.shadow,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      height: "100%",
      justifyContent: "space-between",
    },
    heroHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 30,
      flexWrap: "wrap",
      gap: 20,
    },
    heroTitleSection: {
      flexDirection: "row",
      alignItems: "center",
      flex: 1,
      minWidth: 300,
    },
    heroTitle: {
      fontSize: 28,
      fontWeight: "bold",
      color: currentTheme?.theme?.font_color,
    },
    heroSubtitle: {
      fontSize: 16,
      color:
        currentTheme?.theme?.secondary_font_color ||
        currentTheme?.theme?.font_color,
      marginTop: 4,
    },
    heroEnterButton: {
      paddingHorizontal: 30,
      paddingVertical: 12,
    },
    heroStats: {
      flexDirection: "row",
      justifyContent: "space-around",
      marginBottom: 30,
      flexWrap: "wrap",
      gap: 20,
    },
    statCard: {
      alignItems: "center",
      padding: 20,
      backgroundColor:
        currentTheme?.theme?.background_light ||
        currentTheme?.theme?.backgroundColor,
      borderRadius: 8,
      minWidth: 120,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color,
    },
    statNumber: {
      fontSize: 18,
      fontWeight: "bold",
      marginTop: 8,
      color: currentTheme?.theme?.primary,
    },
    statLabel: {
      fontSize: 14,
      color:
        currentTheme?.theme?.secondary_font_color ||
        currentTheme?.theme?.font_color,
      marginTop: 4,
    },
    heroActions: {
      flexDirection: "row",
      justifyContent: "center",
      gap: 15,
      flexWrap: "wrap",
    },
    heroSecondaryButton: {
      paddingHorizontal: 20,
      minWidth: 250,
    },
    supplementaryContent: {
      flexDirection: isLargeScreen ? "row" : "column",
      gap: 20,
      // height: 400,
      maxWidth: 1280,
      margin: "auto",
    },
    quickActionsContainer: {
      flex: 0.3,
      height: "100%",
      backgroundColor: currentTheme?.theme?.backgroundColor,
      borderRadius: 12,
      padding: 20,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: "bold",
      marginBottom: 15,
      color: currentTheme?.theme?.font_color,
    },
    actionGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 15,
      justifyContent: "space-around",
    },
    actionCard: {
      alignItems: "center",
      padding: 15,
      backgroundColor:
        currentTheme?.theme?.background_light ||
        currentTheme?.theme?.backgroundColor,
      borderRadius: 8,
      minWidth: 100,
      cursor: "pointer",
      borderWidth: 1,
      transition: "border-color 0.2s ease",
    },
    actionLabel: {
      marginTop: 8,
      fontSize: 12,
      textAlign: "center",
      color: currentTheme?.theme?.font_color,
    },
    activityContainer: {
      flex: 1,
      backgroundColor: currentTheme?.theme?.backgroundColor,
      borderRadius: 12,
      padding: 20,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color,
    },
    activityList: {
      gap: 12,
    },
    activityItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: 12,
      padding: 10,
      backgroundColor:
        currentTheme?.theme?.background_light ||
        currentTheme?.theme?.backgroundColor,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: currentTheme?.theme?.border_color,
    },
    activityText: {
      fontSize: 14,
      color:
        currentTheme?.theme?.secondary_font_color ||
        currentTheme?.theme?.font_color,
    },
  });
};

export default WorkspacesScreen;
